import { ref } from 'vue';

import { administrationApiSdk } from '../sdk/administrationClient.js';

import { defineCollectionStore } from './collectionStore.js';

/**
 * Dealerships — the rooftops that buy and sell.
 *
 * `setClaims` replaces the claim set whole, because that is what the route is. A
 * caller adding one claim sends the others back, and this store does not pretend
 * otherwise by offering an `addClaim` that would read the set, append and write
 * — two callers doing that at once lose one of the two claims, and the store
 * cannot see that happen.
 */
export const useDealershipsStore = defineCollectionStore({
    id: 'administration-dealerships',
    client: administrationApiSdk.dealerships,
    extend({ client, attempt, holdCurrent }) {
        const auctionHouses = ref([]);
        const auctionHouseCount = ref(0);

        async function loadAuctionHouses(id, pagination) {
            const attempted = await attempt(function () {
                return client.listAuctionHouses(id, pagination);
            });
            if (attempted.ok === false) {
                return false;
            }
            auctionHouses.value = attempted.value.documents;
            auctionHouseCount.value = attempted.value.meta.totalCount;
            return true;
        }

        return {
            auctionHouses,
            auctionHouseCount,
            loadAuctionHouses,

            setLegalCompany: (id, companyId) => holdCurrent(() => client.setLegalCompany(id, companyId)),
            setBillingCompany: (id, companyId, subscriptionId) => holdCurrent(
                () => client.setBillingCompany(id, companyId, subscriptionId)
            ),
            setOperation: (id, operationId) => holdCurrent(() => client.setOperation(id, operationId)),
            setPolicy: (id, policyName, policy) => holdCurrent(() => client.setPolicy(id, policyName, policy)),
            clearPolicy: (id, policyName) => holdCurrent(() => client.clearPolicy(id, policyName)),
            setClaims: (id, claims) => holdCurrent(() => client.setClaims(id, claims))
        };
    }
});

export default useDealershipsStore;
